import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { KanbanStage } from "@/components/jobs/kanban/types/index";
import {
	COLUMN_COLOR_PALETTE,
	KanbanColumnColor,
	KanbanColumnDef,
	SYSTEM_MATCH_COLUMNS,
	fetchKanbanColumns,
	getMatchStatusLabel,
	repairMatchColumns
} from "@/integrations/supabase/kanbanColumnsApi";

export const kanbanColumnsQueryKey = (organizationId?: string) => ["kanban-columns", organizationId];

export function useKanbanColumns() {
	const { currentOrganization } = useAuth();
	const organizationId = currentOrganization?.id;
	const { t, i18n } = useTranslation();

	const { data, isLoading } = useQuery({
		queryKey: kanbanColumnsQueryKey(organizationId),
		queryFn: () => fetchKanbanColumns(organizationId!),
		enabled: !!organizationId,
		staleTime: 5 * 60 * 1000
	});

	// Full repaired column list (includes hidden columns) for admin/label use
	const columns: KanbanColumnDef[] = useMemo(
		() => repairMatchColumns(data?.matches),
		[data]
	);

	// Visible columns as KanbanStage[] with resolved labels, for the board
	const stages: KanbanStage[] = useMemo(
		() => columns
			.filter((c) => !c.hidden)
			.map((c) => ({ id: c.id, name: getMatchStatusLabel(t, c.id, columns, i18n.language) })),
		[columns, t, i18n.language]
	);

	const statusConfig: Record<string, KanbanColumnColor> = useMemo(() => {
		const config: Record<string, KanbanColumnColor> = {};
		for (const c of columns)
			if (c.color && COLUMN_COLOR_PALETTE[c.color])
				config[c.id] = COLUMN_COLOR_PALETTE[c.color];
		return config;
	}, [columns]);

	const stageIds = useMemo(() => stages.map((s) => s.id), [stages]);

	const getStatusLabel = useCallback(
		(statusId: string) => getMatchStatusLabel(t, statusId, columns, i18n.language),
		[t, columns, i18n.language]
	);

	const hasCustomColumns = useMemo(
		() => columns.length !== SYSTEM_MATCH_COLUMNS.length || columns.some((c) => !c.isSystem || c.hidden || c.color),
		[columns]
	);

	return {
		organizationId,
		isLoading,
		columns,
		stages,
		stageIds,
		statusConfig,
		getStatusLabel,
		hasCustomColumns
	};
}
